import {useMemo, useState} from 'react';
import {useIntl} from 'react-intl';
import {ColumnDef} from '@tanstack/react-table';
import {useMutation, useQuery, useQueryClient} from 'react-query';
import {CommonFile, deleteFile, getCommonFiles, toggleFileVisibility} from './api';
import {formatDate} from '../../../common/tools/date';
import {formatBytes} from '../../../common/tools/filesize';
import {capitalizeFirstLetter} from '../../../common/tools/text';
import ToggleCommonDocumentVisibility from './ToggleCommonDocumentVisibility';
import toast from 'react-hot-toast';
import DownloadFile from '../../documents/DownloadFile';
import CopyFileLink from '../../../../Domains/Document/Components/CopyFileLink';
import TableMetronic from '../../../common/components/table/TableMetronic';
import DeleteFile from '../../documents/DeleteFile';
import RenameFile from '../../documents/RenameFile';
import Button, {faIconAdd} from '../../../../Components/Button';
import FilterSelect, {FilterOption} from '../../../common/components/select/FilterSelect';

interface CommonFilesListProps {
  handleShow: () => void;
}

type LangueFilter = 'all' | 'fr' | 'en' | 'de' | 'es' | 'it';
type CategoryFilter =
  | 'all'
  | 'commercial'
  | 'technique'
  | 'administratif'
  | 'fibre'
  | 'rapportRoc'
  | 'serviceClient';

const CommonFilesList = ({handleShow}: CommonFilesListProps): JSX.Element => {
  const intl = useIntl();
  const [langueFilter, setLangueFilter] = useState<LangueFilter>('all');
  const [categoryFilter, setCategoryFilter] = useState<CategoryFilter>('all');

  const useGetCommonFiles = () => {
    return useQuery(['get-common-files'], async () => getCommonFiles());
  };
  const {isLoading, data} = useGetCommonFiles();

  const langueOptions: FilterOption<LangueFilter>[] = [
    {value: 'all', label: intl.formatMessage({id: 'ADMIN.DOCUMENTS.FILTER.ALL_LANGUES'})},
    {value: 'fr', label: intl.formatMessage({id: 'DOCUMENTS.LANGUES.FR'})},
    {value: 'en', label: intl.formatMessage({id: 'DOCUMENTS.LANGUES.EN'})},
    {value: 'de', label: intl.formatMessage({id: 'DOCUMENTS.LANGUES.DE'})},
    {value: 'es', label: intl.formatMessage({id: 'DOCUMENTS.LANGUES.ES'})},
    {value: 'it', label: intl.formatMessage({id: 'DOCUMENTS.LANGUES.IT'})},
  ];

  const categoryOptions: FilterOption<CategoryFilter>[] = [
    {value: 'all', label: intl.formatMessage({id: 'ADMIN.DOCUMENTS.FILTER.ALL_CATEGORIES'})},
    {value: 'commercial', label: intl.formatMessage({id: 'DOCUMENTS.CATEGORIES.COMMERCIAL'})},
    {value: 'technique', label: intl.formatMessage({id: 'DOCUMENTS.CATEGORIES.TECHNICAL'})},
    {
      value: 'administratif',
      label: intl.formatMessage({id: 'DOCUMENTS.CATEGORIES.ADMINISTRATIVE'}),
    },
    {value: 'fibre', label: intl.formatMessage({id: 'DOCUMENTS.CATEGORIES.FIBER'})},
    {value: 'rapportRoc', label: intl.formatMessage({id: 'DOCUMENTS.CATEGORIES.RAPPORT_ROC'})},
    {
      value: 'serviceClient',
      label: intl.formatMessage({id: 'DOCUMENTS.CATEGORIES.CUSTOMER_SERVICE'}),
    },
  ];

  const filteredData = useMemo(() => {
    if (!data) {
      return data;
    }
    if (langueFilter === 'all' && categoryFilter === 'all') {
      return data;
    }
    return data.filter((file) => {
      if (langueFilter !== 'all' && file.langue !== langueFilter) {
        return false;
      }
      if (categoryFilter !== 'all' && file.category !== categoryFilter) {
        return false;
      }
      return true;
    });
  }, [data, langueFilter, categoryFilter]);

  const columns = useMemo<ColumnDef<CommonFile>[]>(() => {
    const useToggleFileVisibility = (file: CommonFile) => {
      const queryClient = useQueryClient();

      return useMutation({
        mutationFn: () => toggleFileVisibility(file.id),
        mutationKey: ['toggleCommonFileVisibility', file.id],
        onSuccess: (updatedFile) => {
          if (!data || !updatedFile) {
            return;
          }
          let index = data.findIndex((fileToUpdate) => fileToUpdate.id === file.id);
          if (index >= 0) {
            const newData: CommonFile[] = JSON.parse(JSON.stringify(data));
            newData[index] = updatedFile;
            queryClient.setQueryData('get-common-files', newData);
          }
        },
        onError: () => {
          toast.error(intl.formatMessage({id: 'ADMIN.DOCUMENTS.TABLE.TOGGLE.FAIL'}));
          queryClient.invalidateQueries(['get-common-files']);
        },
      });
    };

    return [
      {
        header: intl.formatMessage({id: 'DOCUMENTS.TABLE.TYPE'}),
        accessorKey: 'type',
        size: 70,
      },
      {
        header: intl.formatMessage({id: 'DOCUMENTS.TABLE.FILE'}),
        accessorKey: 'fileName',
        cell: (info) => info.getValue(),
      },
      {
        header: intl.formatMessage({id: 'DOCUMENTS.TABLE.CATEGORY'}),
        accessorKey: 'category',
        cell: (info) => capitalizeFirstLetter(info.getValue() as string),
        size: 100,
      },
      {
        header: intl.formatMessage({id: 'DOCUMENTS.TABLE.LANGUE'}),
        accessorKey: 'langue',
        cell: (info) => info.getValue(),
        size: 70,
      },
      {
        header: intl.formatMessage({id: 'DOCUMENTS.TABLE.START_DATE'}),
        accessorKey: 'start_date',
        cell: (info) => formatDate(info.getValue() as string),
        size: 120,
      },
      {
        header: intl.formatMessage({id: 'DOCUMENTS.TABLE.CREATION_DATE'}),
        accessorKey: 'created_at',
        cell: (info) => formatDate(info.getValue() as string),
        size: 120,
      },
      {
        header: intl.formatMessage({id: 'DOCUMENTS.TABLE.FILESIZE'}),
        accessorKey: 'fileSize',
        cell: (info) => formatBytes(info.getValue() as number),
        size: 110,
      },
      {
        header: intl.formatMessage({id: 'TABLE.HEADER.AVAILABLE'}),
        accessorKey: 'visible',
        cell: (info) => (
          <ToggleCommonDocumentVisibility
            toggleHandler={useToggleFileVisibility}
            file={info.row.original}
          />
        ),
        size: 85,
        enableSorting: true,
      },
      {
        header: intl.formatMessage({id: 'TABLE.HEADER.ACTION'}),
        cell: (info) => (
          <div className="d-flex align-items-center gap-2">
            <DownloadFile file={info.row.original} />
            <CopyFileLink file={info.row.original} />
            <RenameFile file={info.row.original} />
            <DeleteFile
              fileId={info.row.original.id.toString()}
              onClick={() => deleteFile(info.row.original.id)}
            />
          </div>
        ),
        enableSorting: false,
      },
    ];
  }, [intl, data]);

  return (
    <>
      <TableMetronic
        key={`${langueFilter}-${categoryFilter}`}
        title={intl.formatMessage({id: 'ADMIN.DOCUMENTS.LIST.TITLE'})}
        data={filteredData ?? []}
        columns={columns}
        isLoading={isLoading}
        autoResetPage={false}
        pagination={true}
        smallSearchBar={true}
        stackedHeader
        toolbar={
          <>
            <FilterSelect<LangueFilter>
              options={langueOptions}
              value={langueFilter}
              onChange={setLangueFilter}
            />
            <FilterSelect<CategoryFilter>
              options={categoryOptions}
              value={categoryFilter}
              onChange={setCategoryFilter}
              formatOptionLabel={(option) => capitalizeFirstLetter(option.label)}
            />
          </>
        }
        toolbarEnd={
          <Button
            onClick={() => handleShow()}
            faIcon={faIconAdd}
            label={intl.formatMessage({id: 'COMMON.ADD'})}
          />
        }
      />
    </>
  );
};

export default CommonFilesList;
